import React, { useState, useCallback } from 'react';
import { View, StyleSheet } from 'react-native';
import { Text, Card, Button, useTheme, ProgressBar } from 'react-native-paper';
import {
  Target,
  TrendingUp,
  Users,
  Star,
  Brain,
  Settings,
} from 'lucide-react-native';
import type { RecommendationExplanation as RecommendationExplanationType } from '@coffeeplexity/shared';
import { ConfidenceIndicator } from './ConfidenceIndicator';
import { logger } from '../../utils/logger';

interface RecommendationExplanationProps {
  explanation: RecommendationExplanationType;
  confidence: number;
  onPreferenceUpdate?: (preferences: RecommendationPreferences) => void;
}

interface RecommendationPreferences {
  contentTypes: string[];
  difficultyRange: [number, number];
  topicInterests: string[];
  timePreferences: number[];
}

export function RecommendationExplanation({
  explanation,
  confidence,
  onPreferenceUpdate,
}: RecommendationExplanationProps) {
  const theme = useTheme();
  const [showPreferences, setShowPreferences] = useState(false);

  const handlePreferenceToggle = useCallback(() => {
    setShowPreferences(!showPreferences);
  }, [showPreferences]);

  const getAlignmentColor = (score: number) => {
    if (score >= 0.8) return '#4ECDC4'; // High alignment - Teal
    if (score >= 0.6) return '#FF6B35'; // Medium alignment - Orange
    return '#E74C3C'; // Low alignment - Red
  };

  const getDifficultyFitColor = (fit: number) => {
    if (fit >= 0.8) return '#4ECDC4'; // Perfect fit
    if (fit >= 0.6) return '#FFA726'; // Good fit
    return '#FF7043'; // Challenging but manageable
  };

  return (
    <Card
      style={[
        styles.container,
        {
          backgroundColor: theme.colors.surface,
          borderWidth: 1,
          borderColor: theme.colors.outline + '20',
        },
      ]}
    >
      <Card.Content style={styles.content}>
        {/* Primary Reason */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Brain size={16} color={theme.colors.primary} />
            <Text
              variant='labelMedium'
              style={[styles.sectionTitle, { color: theme.colors.primary }]}
            >
              AI REASONING
            </Text>
            <ConfidenceIndicator
              confidence={confidence}
              size='small'
              showLabel={false}
            />
          </View>

          <Text
            variant='bodySmall'
            style={[
              styles.primaryReason,
              { color: theme.colors.onSurfaceVariant },
            ]}
          >
            {explanation.primaryReason}
          </Text>
        </View>

        {/* Skill Alignment */}
        {explanation.skillAlignment.length > 0 && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Target size={16} color={theme.colors.secondary} />
              <Text
                variant='labelMedium'
                style={[styles.sectionTitle, { color: theme.colors.secondary }]}
              >
                SKILL MATCH
              </Text>
            </View>

            <View style={styles.skillList}>
              {explanation.skillAlignment.slice(0, 3).map(skill => (
                <View key={skill.skillId} style={styles.skillItem}>
                  <View style={styles.skillInfo}>
                    <Text
                      variant='bodySmall'
                      style={[
                        styles.skillName,
                        { color: theme.colors.onSurface },
                      ]}
                    >
                      {skill.skillName}
                    </Text>
                    <Text
                      variant='bodySmall'
                      style={[
                        styles.skillScore,
                        { color: getAlignmentColor(skill.alignmentScore) },
                      ]}
                    >
                      {Math.round(skill.alignmentScore * 100)}% match
                    </Text>
                  </View>

                  <ProgressBar
                    progress={skill.alignmentScore}
                    color={getAlignmentColor(skill.alignmentScore)}
                    style={styles.skillProgress}
                  />
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Difficulty Fit */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <TrendingUp size={16} color={theme.colors.tertiary} />
            <Text
              variant='labelMedium'
              style={[styles.sectionTitle, { color: theme.colors.tertiary }]}
            >
              DIFFICULTY FIT
            </Text>
            <Text
              variant='bodySmall'
              style={[
                styles.difficultyInline,
                { color: getDifficultyFitColor(explanation.difficultyFit) },
              ]}
            >
              {Math.round(explanation.difficultyFit * 100)}% fit for your level
            </Text>
          </View>

          <View style={styles.difficultyFit}>
            <View style={styles.progressContainer}>
              <ProgressBar
                progress={explanation.difficultyFit}
                color={getDifficultyFitColor(explanation.difficultyFit)}
                style={styles.difficultyProgress}
              />
            </View>
          </View>
        </View>

        {/* Personalized Factors */}
        {explanation.personalizedFactors.length > 0 && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Users size={16} color={theme.colors.outline} />
              <Text
                variant='labelMedium'
                style={[styles.sectionTitle, { color: theme.colors.outline }]}
              >
                PERSONALIZED FOR YOU
              </Text>
            </View>

            <View style={styles.factorsList}>
              {explanation.personalizedFactors
                .slice(0, 3)
                .map((factor, index) => (
                  <View key={index} style={styles.factorItem}>
                    <Star size={12} color={theme.colors.primary} />
                    <Text
                      variant='bodySmall'
                      style={[
                        styles.factorText,
                        { color: theme.colors.onSurfaceVariant },
                      ]}
                    >
                      {factor}
                    </Text>
                  </View>
                ))}
            </View>
          </View>
        )}

        {/* Alternative Options */}
        {explanation.alternativeOptions.length > 0 && (
          <View style={[styles.section, styles.lastSection]}>
            <Text
              variant='labelSmall'
              style={[
                styles.alternativesLabel,
                { color: theme.colors.onSurfaceVariant },
              ]}
            >
              Other options considered:{' '}
              {explanation.alternativeOptions.slice(0, 2).join(', ')}
            </Text>
          </View>
        )}

        {/* Preference Adjustment */}
        {onPreferenceUpdate && (
          <View
            style={[
              styles.section,
              explanation.alternativeOptions.length === 0
                ? styles.lastSection
                : {},
            ]}
          >
            <Button
              mode='text'
              onPress={handlePreferenceToggle}
              style={styles.preferencesButton}
              labelStyle={styles.preferencesButtonLabel}
              icon={Settings}
              compact
            >
              {showPreferences ? 'Hide' : 'Adjust'} Preferences
            </Button>

            {showPreferences && (
              <View style={styles.preferencesContainer}>
                <Text
                  variant='bodySmall'
                  style={[
                    styles.preferencesText,
                    { color: theme.colors.onSurfaceVariant },
                  ]}
                >
                  Help us improve recommendations by adjusting your preferences.
                </Text>
                <Button
                  mode='outlined'
                  onPress={() => {
                    // This would open a preferences modal or navigate to preferences screen
                    logger.log('Open preferences');
                  }}
                  style={styles.preferencesActionButton}
                  labelStyle={styles.preferencesActionLabel}
                  compact
                >
                  Update Preferences
                </Button>
              </View>
            )}
          </View>
        )}
      </Card.Content>
    </Card>
  );
}

const styles = StyleSheet.create({
  container: {
    marginTop: 8,
    elevation: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
  },
  content: {
    paddingVertical: 20,
    paddingHorizontal: 16,
  },
  section: {
    marginBottom: 24,
  },
  lastSection: {
    marginBottom: 0,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 14,
  },
  sectionTitle: {
    fontWeight: '600',
    fontSize: 11,
    flex: 1,
    letterSpacing: 0.5,
  },
  primaryReason: {
    lineHeight: 20,
    fontStyle: 'italic',
    fontSize: 12,
    marginTop: 2,
  },
  skillList: {
    gap: 16,
  },
  skillItem: {
    paddingVertical: 6,
    flexDirection: 'column',
  },
  skillInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  skillName: {
    fontWeight: '500',
    fontSize: 12,
  },
  skillScore: {
    fontSize: 11,
    fontWeight: '600',
  },
  skillProgress: {
    height: 4,
    borderRadius: 2,
    marginBottom: 2,
  },
  chipContainer: {
    marginTop: 8,
    width: '100%',
  },
  gapChip: {
    height: 22,
    alignSelf: 'flex-start',
  },
  gapChipText: {
    fontSize: 9,
    fontWeight: '600',
    lineHeight: 12,
  },
  difficultyFit: {
    gap: 14,
  },
  progressContainer: {
    marginTop: 2,
  },
  difficultyProgress: {
    height: 6,
    borderRadius: 3,
  },
  difficultyText: {
    fontSize: 11,
    fontWeight: '600',
    marginTop: 2,
    alignSelf: 'flex-end',
    textAlign: 'right',
  },
  difficultyInline: {
    fontSize: 11,
    fontWeight: '600',
    marginLeft: 'auto',
    textAlign: 'right',
  },
  factorsList: {
    gap: 10,
  },
  factorItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 4,
  },
  factorText: {
    fontSize: 11,
    flex: 1,
    lineHeight: 16,
  },
  alternativesLabel: {
    fontSize: 10,
    fontStyle: 'italic',
    lineHeight: 14,
    marginTop: 4,
  },
  preferencesButton: {
    alignSelf: 'flex-start',
    marginLeft: -8,
  },
  preferencesButtonLabel: {
    fontSize: 11,
  },
  preferencesContainer: {
    backgroundColor: 'rgba(78, 205, 196, 0.05)',
    padding: 12,
    borderRadius: 8,
    marginTop: 12,
    gap: 10,
  },
  preferencesText: {
    fontSize: 10,
    lineHeight: 14,
  },
  preferencesActionButton: {
    alignSelf: 'flex-start',
  },
  preferencesActionLabel: {
    fontSize: 10,
  },
});
